import React, { useRef, useMemo, useState } from "react";
import { useDimensions } from "common/hooks";
import styled from "styled-components";
import * as d3 from "d3";
import { createRectanglePath } from "common/utils";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import Legend from "common/graphs/Legend";

const CHART_HEIGHT = 340;
const CHART_MARGIN = { top: 10, right: 40, bottom: 70, left: 50 };
const Y_AXIS_UNIT = "Mbps";
const RADIUS = 5;
const PADDING_Y_AXIS = 10;
const MAX_PLOT_WIDTH = 60;

const ChartContainer = styled.div`
  position: relative;
`;

const WrapperChart = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  svg {
    width: 100%;
    height: 100%;

    .lineOrdinalMultipleAxis {
      fill: var(--chart-axis-text-color);
      color: #929295;
      font-family: var(--chart-font-family, Arial, sans-serif);
      font-size: 9px !important;

      text {
        font-size: 9px !important;
      }

      .clickable {
        cursor: pointer;
      }

      circle {
        stroke: #929295;
      }

      &.xAxis {
        text {
          font-size: 11px !important;
        }
      }
    }
    .tooltip:hover {
      cursor: pointer;
    }
  }
`;

const LabelYUnit = styled.div`
  position: absolute;
  transform: rotate(270deg);
  top: 45%;
  left: -10px;
`;

const Empty = styled.div`
  width: 100%;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const StyledLegendContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: flex-end !important;
  padding-right: 40px;
  z-index: 10;
`;

const FIELDS = [
  { name: "min", label: "Minimum" },
  { name: "p10", label: "10th Percentile" },
  { name: "p25", label: "25th Percentile" },
  { name: "p50", label: "Median" },
  { name: "p75", label: "75th Percentile" },
  { name: "p90", label: "90th Percentile" },
  { name: "max", label: "Maximum" },
];

function LineChartOrdinal({ data, selection, setSelection, xField = "app",  formatXField, yAxisUnit=Y_AXIS_UNIT, minmax=true}) {
  const chartRef = useRef(null);
  const dimensions = useDimensions(chartRef);
  const [hoveredCell, setHoveredCell] = useState(null);
  const { width } = dimensions;
  const height = CHART_HEIGHT;
  const margin = CHART_MARGIN;
  const widthChart = width - margin.left - margin.right;
  const heightChart = height - margin.top - margin.bottom;
  const colorsSet = login.isTheme("light") ? colors : colorsDark;
  const hasSelection = selection && setSelection;
  const showFields = minmax ? FIELDS : FIELDS.slice(1, 6);
  const fields = showFields.map((field, index) => {
    return {
      ...field,
      color: colorsSet[index],
    };
  });
  const [filterFields, setFilterFields] = useState(
    new Set(fields.map((field) => field.name))
  );

  if (!data || data.length === 0) {
    return <Empty>No Data Available</Empty>;
  }

  const filteredData = hasSelection ? data.filter((item) => selection.has(item[xField])): data;

  const series = fields.map((field) => {
    const { name, color } = field;
    const values = filteredData.map((item) => {
      const x = formatXField ? formatXField(item[xField]) : item[xField];
      return { x, y: item[name] };
    });
    return {
      key: name,
      color,
      values,
    };
  });

  const selectedSeries = series.filter((item) => filterFields.has(item.key));

  const apps = data.map((item) => formatXField ? formatXField(item[xField]): item[xField]);
  const MinMaxValues = filteredData.reduce((acc, item) => {
    for (const key in item) {
      if(filterFields.has(key)){
        acc.push(item[key])
      }
    }
    return acc;
  }, []);
  const minMax = d3.extent(MinMaxValues);

  const yScale = useMemo(() => {
    return d3.scaleLinear().domain(minMax).range([heightChart, 0]);
  }, [minMax[0], minMax[1], heightChart]);

  const xScale = useMemo(() => {
    return d3.scaleBand().range([0, widthChart]).domain(apps).padding(0.01);
  }, [apps, width]);

  const yLabelsTicks = yScale.ticks(5).map((tick, i) => {
    return (
      <g key={tick}>
        <text x={-10} y={yScale(tick) + 3} fontSize={9} textAnchor="end">
          {tick}
        </text>
      </g>
    );
  });

  const handleSelect = (name, selectionType) => {
    if (selectionType === "single") {
      if(selection.size === 1){
        const newFilterSet = new Set(data.map((item) => item[xField]));
        setSelection(newFilterSet);
      } else {
        const newFilterSet = new Set(selection);
        if (newFilterSet.has(name)) {
          newFilterSet.delete(name);
        } else {
          newFilterSet.add(name);
        }
        setSelection(newFilterSet);
      }
    } else {
      const newFilterSet = new Set([name]);
      setSelection(newFilterSet);
    }
  }

  const xLabelsTicks = apps.map((name, i) => {
    const x = xScale(name);

    if (!x) {
      return null;
    }

    return (
      <g key={name}>
        <line
          x1={x + xScale.bandwidth() / 2}
          x2={x + xScale.bandwidth() / 2}
          y1={heightChart - 2 + PADDING_Y_AXIS / 2}
          y2={heightChart + 4 + PADDING_Y_AXIS / 2}
          stroke="var(--chart-axis-text-color)"
        />
        <g
          transform={`translate(${x + xScale.bandwidth() / 2},${
            heightChart + 20
          })`}
          onClick={() => handleSelect(name, "single")}
          onDoubleClick={() => handleSelect(name, "double")}
          className="clickable"
        >          
          {hasSelection ? <circle
            cx={2}
            cy={-2}
            r="4"
            fill={selection.has(name) ? "#929295" : "transparent"}
          /> : null}
          <text x={hasSelection ? 8 : 0} y={0} textAnchor="begin" transform="rotate(30)">
            {name}
          </text>
        </g>
      </g>
    );
  });
  const lineBuilder = d3
    .line()
    .x((d) => xScale(d.x) + xScale.bandwidth() / 2)
    .y((d) => yScale(d.y))
    .curve(d3.curveLinear);

  const seriesLines = selectedSeries.map((item, i) => {
    const { key, values, color } = item;
    const filteredValues = values.filter(({x, y}) => (!isNaN(y)));
    const linePlotPath = lineBuilder(filteredValues);
    const linePlot = (
      <path d={linePlotPath} stroke={color} fill="none" strokeWidth={2} />
    );
    return <g key={key}>{linePlot}</g>;
  });

  const seriesDots = selectedSeries.map((item, i) => {
    const { key, values, color } = item;
    return values.map((item, i) => {
      if(isNaN(item.y)){
        return null;
      }
      return (
        <g key={`${key}-${i}`}>
          <circle
            cx={xScale(item.x) + xScale.bandwidth() / 2}
            cy={yScale(item.y)}
            r="7"
            stroke={"transparent"}
            fill={"transparent"}
            onMouseEnter={(e) => {
              setHoveredCell({
                name: item.x,
                value: item.y,
                key,
                xPos: xScale(item.x) + xScale.bandwidth() / 2,
                yPos: yScale(item.y),
              });
            }}
            onMouseLeave={() => {
              setHoveredCell(null);
            }}
            className="tooltip"
          />
          <circle
            cx={xScale(item.x) + xScale.bandwidth() / 2}
            cy={yScale(item.y)}
            r="2"
            stroke={color}
            fill={color}
          />
        </g>
      );
    });
  });

  return (
    <ChartContainer>
      <StyledLegendContainer>
        <Legend
          fields={fields}
          onChange={(filter) => {
            setFilterFields(filter);
          }}
          value={filterFields}
        />
      </StyledLegendContainer>
      <WrapperChart ref={chartRef} style={{ height }}>
        <LabelYUnit>{yAxisUnit}</LabelYUnit>
        <svg>
          <g
            width={width}
            height={height}
            transform={`translate(${[
              margin.left,
              margin.top - PADDING_Y_AXIS / 2,
            ].join(",")})`}
          >
            <rect
              x={0}
              y={0}
              rx={5}
              width={widthChart}
              height={heightChart + PADDING_Y_AXIS}
              fill={"transparent"}
              stroke={"var(--chart-inner-border-color, #000)"}
            />
          </g>
          <g
            width={width}
            height={height}
            transform={`translate(${[margin.left, margin.top].join(",")})`}
          >
            <g className="lineOrdinalMultipleAxis xAxis">{xLabelsTicks}</g>
            <g className="lineOrdinalMultipleAxis yAxis">{yLabelsTicks}</g>
            <g className="lineOrdinals">{seriesLines}</g>
            <g className="lineOrdinals">{seriesDots}</g>
          </g>
        </svg>
        <Tooltip interactionData={hoveredCell} unit={yAxisUnit} />
      </WrapperChart>
    </ChartContainer>
  );
}

export default LineChartOrdinal;

const TooltipWrapper = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  font-size: 12px;
  padding: 6px;
  transform: translate(-50%, -50%);
  font-family: "AllianceNo2-Regular" !important;
  z-index: 200;
`;

const TooltipHeader = styled.div`
  font-family: "AllianceNo2-SemiBold" !important;
  font-size: 13px;
  font-weight: 100 !important;
`;

const TooltipValueWrapper = styled.div`
  display: flex;
  width: 180px;
  justify-content: space-between;
`;

const TooltipValue = styled.div`
  font-family: "AllianceNo2-SemiBold" !important;
`;

const TooltipLabel = styled.div`
  margin: 0 0px;
`;

const TooltipValues = styled.div`
  margin-top: 10px;
`;

const TOOLTIP_VALUES = {
  p90: "90th percentile",
  p75: "75th percentile",
  p50: "Median",
  p25: "25th percentile",
  p10: "10th percentile",
  min: "Minimum",
  max: "Maximum",
};

const Tooltip = ({ interactionData, unit }) => {
  if (!interactionData) {
    return null;
  }

  const { key, value, xPos, yPos, name } = interactionData;

  return (
    <div
      style={{
        height: "auto",
        width: "auto",
        position: "absolute",
        top: 0,
        left: interactionData.xPos,
        pointerEvents: "none",
      }}
    >
      <TooltipWrapper
        style={{
          position: "absolute",
          width: "auto",
          left: 60,
          top: 30,
        }}
      >
        <TooltipHeader>{name}</TooltipHeader>
        <TooltipValues>
          <TooltipValueWrapper key={`${key}-value`}>
            <TooltipLabel>{TOOLTIP_VALUES[key]}</TooltipLabel>
            <TooltipValue>
              {value === "n/a" ? "n/a" : `${d3.format(".2f")(value)} ${unit}`}
            </TooltipValue>
          </TooltipValueWrapper>
        </TooltipValues>
      </TooltipWrapper>
    </div>
  );
};